import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import './App.css';
import './styles/ClientProfile.css';
import { useAuth } from './context/AuthContext';
import { canAccessModule } from './config/permissions';
import type { AuthContextType } from './types/auth';
import ProtectedRoute from './components/common/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import ClientListPage from './pages/ClientListPage';
import ClientProfilePage from './pages/ClientProfilePage';
import ClientTrashPage from './pages/ClientTrashPage';
import ServiceListPage from './pages/ServiceListPage';
import ServiceTrashPage from './pages/ServiceTrashPage';
import ServiceCategoryListPage from './pages/ServiceCategoryListPage';
import SalesListPage from './pages/SalesListPage';
import NewSalePage from './pages/NewSalePage';
import SaleDetailPage from './pages/SaleDetailPage';
import ServiceSalesPage from './pages/ServiceSalesPage';
import InvoiceListPage from './pages/InvoiceListPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import UserListPage from './pages/UserListPage';
import UserProfilePage from './pages/UserProfilePage';
import ProposalListPage from './pages/ProposalListPage';
import ProposalDetailPage from './pages/ProposalDetailPage';
import ChatSessionListPage from './pages/ChatSessionListPage';
import ProjectListPage from './pages/ProjectListPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import AIChatPage from './pages/AIChatPage';
import SettingsPage from './pages/SettingsPage';

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/*" element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  );
};

const LoginRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="loading-spinner"></div><p>Cargando...</p></div>;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <LoginPage />;
};

const AppLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSalesMenuOpen, setIsSalesMenuOpen] = useState(false);
  const location = useLocation();
  const { user, roleLabel, logout } = useAuth() as AuthContextType;
  // Sistema de alertas removido temporalmente

  // Cerrar sidebar automáticamente en pantallas grandes
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Abrir menú de ventas automáticamente cuando estemos en una página de ventas
  useEffect(() => {
    const isOnSalesPage = location.pathname.startsWith('/sales');
    setIsSalesMenuOpen(isOnSalesPage);
  }, [location.pathname]);

  // Cerrar sidebar cuando cambia la ruta (en móvil)
  useEffect(() => {
    if (window.innerWidth <= 768) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const getPageTitle = () => {
    switch (location.pathname) {
      case '/clients':
        return 'Gestión de Clientes';
      case '/services':
        return 'Gestión de Servicios';
      case '/services/categories':
        return 'Categorías de Servicios';
      case '/sales':
        return 'Gestión de Ventas';
      case '/sales/new':
        return 'Nueva Venta';
      case (location.pathname.match(/^\/sales\/edit\/[^/]+$/) || {}).input:
        return 'Editar Venta';
      case '/sales/service-sales':
        return 'Ventas por Servicio';
      case '/invoices':
        return 'Gestión de Facturas';
      case '/proposals':
        return 'Propuestas';
      case '/customer-service':
        return 'Atención al Cliente • Consultas Web';
      case '/projects':
        return 'Gestión de Proyectos';
      case '/settings':
        return 'Configuración';
      case '/ai-chat':
        return 'Xtreme-AI';
      case '/dashboard':
        return 'Dashboard';
      case '/users':
        return 'Gestión de Usuarios';
      default:
        if (location.pathname.startsWith('/proposals/')) return 'Detalle de Propuesta';
        if (location.pathname.startsWith('/projects/')) return 'Detalle de Proyecto';
        if (location.pathname.startsWith('/users/')) return 'Perfil de Usuario';
        return 'RX-App';
    }
  };

  const getPageIcon = () => {
    switch (location.pathname) {
      case '/clients':
        return '👥';
      case '/services':
        return '⚙️';
      case '/services/categories':
        return '📂';
      case '/sales':
        return '🛒';
      case '/sales/new':
        return '🛒';
      case (location.pathname.match(/^\/sales\/edit\/[^/]+$/) || {}).input:
        return '🛒';
      case '/sales/service-sales':
        return '📊';
      case '/invoices':
        return '💰';
      case '/proposals':
        return '📝';
      case '/customer-service':
        return '💬';
      case '/settings':
        return '⚙️';
      case '/projects':
        return '📊';
      case '/ai-chat':
        return '🧠';
      case '/dashboard':
        return '📈';
      case '/users':
        return '👤';
      default:
        if (location.pathname.startsWith('/proposals/')) return '📝';
        if (location.pathname.startsWith('/projects/')) return '📊';
        if (location.pathname.startsWith('/users/')) return '👤';
        return '📊';
    }
  };

  return (
    <div className="app-container">
      {/* Sidebar */}
      <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/images/Logotipo-RX-big.png" alt="RxApp" className="sidebar-logo-img" />
            <span className="sidebar-logo-text">RedXtreme Platform</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <ul>
            {canAccessModule(user?.role, 'dashboard') && (
              <li>
                <Link to="/dashboard" className={location.pathname === '/dashboard' ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}>
                  <span className="nav-icon">📈</span>
                  Dashboard
                </Link>
              </li>
            )}
            {canAccessModule(user?.role, 'clientes') && (
              <li>
                <Link to="/clients" className={location.pathname.startsWith('/clients') ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}>
                  <span className="nav-icon">👥</span>
                  Clientes
                </Link>
              </li>
            )}
            {canAccessModule(user?.role, 'servicios') && (
              <li>
                <Link to="/services" className={location.pathname.startsWith('/services') ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}>
                  <span className="nav-icon">⚙️</span>
                  Servicios
                </Link>
              </li>
            )}
            {canAccessModule(user?.role, 'propuestas') && (
              <li>
                <Link to="/proposals" className={location.pathname.startsWith('/proposals') ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}>
                  <span className="nav-icon">📝</span>
                  Propuestas
                </Link>
              </li>
            )}
            {canAccessModule(user?.role, 'chat-sessions') && (
              <li>
                <Link to="/customer-service" className={location.pathname.startsWith('/customer-service') ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}>
                  <span className="nav-icon">💬</span>
                  Atención al Cliente
                </Link>
              </li>
            )}
            {canAccessModule(user?.role, 'ventas') && (
              <li className="nav-group">
                <div className="nav-group-header" onClick={() => setIsSalesMenuOpen(!isSalesMenuOpen)}>
                  <span className="nav-icon">🛒</span>
                  Ventas
                  <span className={`nav-chevron ${isSalesMenuOpen ? 'open' : ''}`}>▼</span>
                </div>
                {isSalesMenuOpen && (
                  <ul className="nav-submenu entering">
                    <li><Link to="/sales" className={location.pathname === '/sales' ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}><span className="nav-submenu-icon">📋</span>Lista de Ventas</Link></li>
                    <li><Link to="/sales/new" className={location.pathname === '/sales/new' ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}><span className="nav-submenu-icon">➕</span>Nueva Venta</Link></li>
                    <li><Link to="/sales/service-sales" className={location.pathname === '/sales/service-sales' ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}><span className="nav-submenu-icon">📊</span>Ventas por Servicio</Link></li>
                  </ul>
                )}
              </li>
            )}
            {canAccessModule(user?.role, 'facturas') && (
              <li>
                <Link to="/invoices" className={location.pathname.startsWith('/invoices') ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}>
                  <span className="nav-icon">💰</span>
                  Facturas
                </Link>
              </li>
            )}
            {canAccessModule(user?.role, 'proyectos') && (
              <li>
                <Link to="/projects" className={location.pathname.startsWith('/projects') ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}>
                  <span className="nav-icon">📊</span>
                  Proyectos
                </Link>
              </li>
            )}
            {canAccessModule(user?.role, 'ai-chat') && (
              <li>
                <Link to="/ai-chat" className={location.pathname === '/ai-chat' ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}>
                  <span className="nav-icon">🧠</span>
                  Xtreme-AI
                </Link>
              </li>
            )}
            {canAccessModule(user?.role, 'usuarios') && (
              <li>
                <Link to="/users" className={location.pathname.startsWith('/users') ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}>
                  <span className="nav-icon">👤</span>
                  Usuarios
                </Link>
              </li>
            )}
            {canAccessModule(user?.role, 'configuracion') && (
              <li>
                <Link to="/settings" className={location.pathname.startsWith('/settings') ? 'active' : ''} onClick={() => setIsSidebarOpen(false)}>
                  <span className="nav-icon">⚙️</span>
                  Configuración
                </Link>
              </li>
            )}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <div>© 2025 RedXtreme</div>
          <div>RX-App v1.0.0</div>
        </div>
      </aside>

      {/* Overlay para móvil */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-1000 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      {/* Main Content */}
      <main className="main-content">
        {/* Header */}
        <header className="main-header">
          <div className="flex items-center gap-4">
            <button 
              onClick={toggleSidebar}
              className="btn btn-outline btn-sm md:hidden"
              style={{ minWidth: '40px', height: '40px' }}
              title="Abrir menú"
            >
              ☰
            </button>
            <div className="flex items-center gap-3">
              <span className="text-2xl">{getPageIcon()}</span>
              <h1 className="header-title">{getPageTitle()}</h1>
            </div>
          </div>
          
          <div className="header-actions">
            <div className="user-profile-dropdown">
              <div className="user-profile">
                <div className="user-avatar">
                  {(user?.personal?.firstName?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                </div>
                <div className="user-info">
                  <div className="user-name">{user?.personal?.firstName ? `${user.personal.firstName} ${user.personal.lastName || ''}`.trim() || user?.email : user?.email}</div>
                  <div className="user-role">{roleLabel || user?.role}</div>
                </div>
              </div>
              <button className="btn btn-sm btn-outline ml-2" onClick={logout} title="Cerrar sesión">
                Salir
              </button>
            </div>
          </div>
      </header>

        {/* Content Area */}
        <div className="content-area">
          <Routes>
            {/* Ruta por defecto redirige a dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* Dashboard */}
            <Route path="/dashboard" element={<DashboardPage />} />
            
            {/* Módulo CRM - Gestión de Clientes */}
            <Route path="/clients" element={<ProtectedRoute module="clientes"><ClientListPage /></ProtectedRoute>} />
            <Route path="/clients/:id" element={<ProtectedRoute module="clientes"><ClientProfilePage /></ProtectedRoute>} />
            <Route path="/clients/trash" element={<ProtectedRoute module="clientes"><ClientTrashPage /></ProtectedRoute>} />
            
            {/* Módulo SERV - Gestión de Servicios */}
            <Route path="/services" element={<ProtectedRoute module="servicios"><ServiceListPage /></ProtectedRoute>} />
            <Route path="/services/trash" element={<ProtectedRoute module="servicios"><ServiceTrashPage /></ProtectedRoute>} />
            <Route path="/services/categories" element={<ProtectedRoute module="servicios"><ServiceCategoryListPage /></ProtectedRoute>} />
            
               {/* Módulo VENTAS - Gestión de Ventas */}
               <Route path="/sales" element={<ProtectedRoute module="ventas"><SalesListPage /></ProtectedRoute>} />
               <Route path="/sales/new" element={<ProtectedRoute module="ventas"><NewSalePage /></ProtectedRoute>} />
               <Route path="/sales/edit/:id" element={<ProtectedRoute module="ventas"><NewSalePage /></ProtectedRoute>} />
               <Route path="/sales/:id" element={<ProtectedRoute module="ventas"><SaleDetailPage /></ProtectedRoute>} />
               <Route path="/sales/service-sales" element={<ProtectedRoute module="ventas"><ServiceSalesPage /></ProtectedRoute>} />
            
            {/* Módulo Propuestas IA */}
            <Route path="/proposals" element={<ProtectedRoute module="propuestas"><ProposalListPage /></ProtectedRoute>} />
            <Route path="/proposals/:id" element={<ProtectedRoute module="propuestas"><ProposalDetailPage /></ProtectedRoute>} />
            
            {/* Módulo Atención al Cliente / Consultas Web */}
            <Route path="/customer-service" element={<ProtectedRoute module="chat-sessions"><ChatSessionListPage /></ProtectedRoute>} />
            
            {/* Módulo Proyectos */}
            <Route path="/projects" element={<ProtectedRoute module="proyectos"><ProjectListPage /></ProtectedRoute>} />
            <Route path="/projects/:id" element={<ProtectedRoute module="proyectos"><ProjectDetailPage /></ProtectedRoute>} />
            
            {/* Módulo IA Global */}
            <Route path="/ai-chat" element={<ProtectedRoute module="ai-chat"><AIChatPage /></ProtectedRoute>} />
            
            {/* Módulo FACTURAS - Gestión de Facturas */}
            <Route path="/invoices" element={<ProtectedRoute module="facturas"><InvoiceListPage /></ProtectedRoute>} />
            <Route path="/invoices/:id" element={<ProtectedRoute module="facturas"><InvoiceDetailPage /></ProtectedRoute>} />
            
            {/* Módulo USUARIOS - Gestión de Usuarios y Accesos */}
            <Route path="/users" element={<ProtectedRoute module="usuarios"><UserListPage /></ProtectedRoute>} />
            <Route path="/users/:id" element={<ProtectedRoute module="usuarios"><UserProfilePage /></ProtectedRoute>} />
            
            {/* Módulo CONFIGURACIÓN - Solo Super Admin */}
            <Route path="/settings" element={<ProtectedRoute module="configuracion"><SettingsPage /></ProtectedRoute>} />
            
            {/* Ruta para casos no encontrados */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

// Componente Dashboard modernizado
const DashboardPage = () => (
  <div className="dashboard-container animate-fadeIn">
    <div className="dashboard-grid">
      <div className="dashboard-card">
        <div className="card-content">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Clientes Activos</h3>
              <p className="text-3xl font-bold text-primary">0</p>
              <p className="text-sm text-secondary mt-1">Total registrados</p>
            </div>
            <div className="text-4xl">👥</div>
          </div>
        </div>
      </div>

      <div className="dashboard-card">
        <div className="card-content">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Proyectos</h3>
              <p className="text-3xl font-bold text-success">0</p>
              <p className="text-sm text-secondary mt-1">En progreso</p>
            </div>
            <div className="text-4xl">📋</div>
          </div>
        </div>
      </div>

      <div className="dashboard-card">
        <div className="card-content">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Facturación</h3>
              <p className="text-3xl font-bold text-info">$0</p>
              <p className="text-sm text-secondary mt-1">Este mes</p>
            </div>
            <div className="text-4xl">💰</div>
          </div>
        </div>
      </div>
    </div>

    <div className="dashboard-grid">
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Resumen de Actividad</h3>
        </div>
        <div className="card-content">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm">✓</div>
                <div>
                  <div className="font-medium">Sistema inicializado</div>
                  <div className="text-sm text-secondary">Plataforma lista para uso</div>
                </div>
              </div>
              <span className="text-sm text-secondary">Hoy</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-info rounded-full flex items-center justify-center text-white text-sm">🔧</div>
                <div>
                  <div className="font-medium">Módulo CRM activo</div>
                  <div className="text-sm text-secondary">Gestión de clientes disponible</div>
                </div>
              </div>
              <span className="text-sm text-secondary">Hoy</span>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Próximos Desarrollos</h3>
        </div>
        <div className="card-content">
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 border border-warning rounded">
              <div className="w-8 h-8 bg-warning rounded-full flex items-center justify-center text-white text-sm">📋</div>
              <div>
                <div className="font-medium">Módulo de Proyectos</div>
                <div className="text-sm text-secondary">Fase 1 - En desarrollo</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 border border-warning rounded">
              <div className="w-8 h-8 bg-warning rounded-full flex items-center justify-center text-white text-sm">💰</div>
              <div>
                <div className="font-medium">Sistema de Facturación</div>
                <div className="text-sm text-secondary">Fase 1 - Planificado</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 border border-info rounded">
              <div className="w-8 h-8 bg-info rounded-full flex items-center justify-center text-white text-sm">🤖</div>
              <div>
                <div className="font-medium">Asistentes AI</div>
                <div className="text-sm text-secondary">Fase 3 - Futuro</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Componente Proyectos modernizado
const ProjectsPage = () => (
  <div className="animate-fadeIn">
    <div className="card text-center">
      <div className="card-content">
        <div className="text-6xl mb-4">📋</div>
        <h2 className="text-2xl font-bold mb-4">Gestión de Proyectos</h2>
        <p className="text-secondary mb-6">
          El módulo de proyectos te permitirá crear, gestionar y dar seguimiento a todos tus proyectos de desarrollo.
        </p>
        <div className="flex justify-center gap-4 mb-6">
          <div className="badge badge-warning">Fase 1</div>
          <div className="badge badge-secondary">En desarrollo</div>
        </div>
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="font-semibold mb-3">Características próximas:</h3>
          <ul className="text-left space-y-2">
            <li className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              Crear y asignar proyectos a clientes
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              Tablero Kanban para gestión de tareas
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              Seguimiento de progreso en tiempo real
            </li>
            <li className="flex items-center gap-2">
              <span className="text-primary">✓</span>
              Colaboración en equipo
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

// Componente Facturas modernizado
const InvoicesPage = () => (
  <div className="animate-fadeIn">
    <div className="card text-center">
      <div className="card-content">
        <div className="text-6xl mb-4">💰</div>
        <h2 className="text-2xl font-bold mb-4">Sistema de Facturación</h2>
        <p className="text-secondary mb-6">
          Gestiona toda tu facturación de manera profesional con generación automática de PDF y facturación recurrente.
        </p>
        <div className="flex justify-center gap-4 mb-6">
          <div className="badge badge-warning">Fase 1</div>
          <div className="badge badge-secondary">Planificado</div>
        </div>
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="font-semibold mb-3">Características próximas:</h3>
          <ul className="text-left space-y-2">
            <li className="flex items-center gap-2">
              <span className="text-success">✓</span>
              Generación automática de facturas PDF
            </li>
            <li className="flex items-center gap-2">
              <span className="text-success">✓</span>
              Facturación recurrente automatizada
            </li>
            <li className="flex items-center gap-2">
              <span className="text-success">✓</span>
              Seguimiento de pagos y vencimientos
            </li>
            <li className="flex items-center gap-2">
              <span className="text-success">✓</span>
              Reportes financieros detallados
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

// Componente 404 modernizado
const NotFoundPage = () => (
  <div className="animate-fadeIn">
    <div className="card text-center">
      <div className="card-content">
        <div className="text-6xl mb-4">🔍</div>
        <h2 className="text-2xl font-bold mb-4">Página no encontrada</h2>
        <p className="text-secondary mb-6">
          La página que buscas no existe o está en desarrollo.
        </p>
        <Link to="/dashboard" className="btn btn-primary">
          Ir al Dashboard
        </Link>
      </div>
    </div>
    
    {/* Sistema de Alertas removido temporalmente */}
  </div>
);

export default App;
